import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { baseUrl } from 'constants/baseUrl';
import { authHeader } from '../../constants/authenticationHeader';

// Default values based on API specs
const defaultCleanSettings = {
  criteria: {
    organization: 'all',
    brands: 'all',
    clients: 'all',
    location: ['all'],
  },
  sort: {
    by: 'title',
    order: 'desc',
  },
};

// The embed script reads sort settings from HLSettings.sort, not HLSettings.criteria
const toEmbedSettings = ({ sort_by, sort_order, ...criteria }) => ({
  criteria,
  sort: { by: sort_by, order: sort_order },
});

// Default values for settings and form data
const defaultSettings = {
  exampleSettingGroup: {
    organization: 'all',
    brands: 'all',
    clients: 'all',
    location: ['all'],
    sort_by: 'title',
    sort_order: 'desc',
  },
};

const defaultFormData = {
  exampleSettingGroup: {
    brands: {
      type: 'select',
      options: ['all', 'specific_org1', 'specific_org2'],
      desc: 'Filter jobs by organization. Default: all.',
    },
    clients: {
      type: 'select',
      options: ['all', 'client1', 'client2'],
      desc: 'Filter jobs by clients. Default: all.',
    },
    location: {
      type: 'select',
      options: ['all', 'location1', 'location2'],
      multiple: true,
      desc: 'Filter jobs by location. Default: all.',
    },
    sort_by: {
      type: 'select',
      options: { title: 'title', country: 'country', state: 'state', city: 'city', created: 'created', modified: 'modified' },
      desc: 'Sort jobs by attribute. Default: title.',
    },
    sort_order: {
      type: 'select',
      options: { desc: 'desc', asc: 'asc' },
      desc: 'Sort order (asc or desc). Default: desc.',
    },
  },
};

// replace http:// or https:// with empty string
const noHttpsBaseUrl = baseUrl.replace(/(^\w+:|^)\/\//, '');

const embedScriptUrl = `${baseUrl}/embed`;

// The code a customer pastes into their own site
const buildEmbedCode = (embedSettings) => `<div class="row"><div id="hl-container"></div></div>
<script>
  HLSettings = ${JSON.stringify(embedSettings)};
  (function(w, d, s) {
    var js, fjs = d.getElementsByTagName(s)[0];
    js = d.createElement(s);
    js.src = "//${noHttpsBaseUrl}/embed";
    js.async = 1;
    fjs.parentNode.insertBefore(js, fjs);
  })(window, document, "script");
</script>`;

const JobsEmbedPage = ({
  cleanSettings = defaultCleanSettings,
  settings = defaultSettings
}) => {
  const [formData, setFormData] = useState(defaultFormData);
  const [formValues, setFormValues] = useState(settings);
  const [isLoading, setIsLoading] = useState(true);
  const [isRestricted, setIsRestricted] = useState(false);
  const [embedSettings, setEmbedSettings] = useState(cleanSettings);
  const outputRef = useRef(null);

  const handleInputChange = (key, label, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [key]: {
        ...prevValues[key],
        [label]: Array.isArray(value) ? value.map(option => option.value) : value, 
      },
    }));
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const headers = authHeader().common;
      const accountResponse = await axios.get(`${baseUrl}/rest/tmr-accounts/current`, { headers });
      if (!accountResponse.data?.data?.capabilities?.can_use_jobs_embed) {
        setIsRestricted(true);
        return;
      }

      const response = await axios.get(`${baseUrl}/rest/jobs-embed`, { headers });

      // Extract criteria from the API response
      const { criteria, sort } = response.data.data.formData;

      // Replace keys in defaultFormData with keys from criteria
      const updatedFormData = {
        ...defaultFormData,
        exampleSettingGroup: {
          ...defaultFormData.exampleSettingGroup,
          ...criteria, // Override specific keys (brands, clients, location, organization)
          ...(sort?.by && { sort_by: { ...defaultFormData.exampleSettingGroup.sort_by, options: sort.by.options } }),
          ...(sort?.order && { sort_order: { ...defaultFormData.exampleSettingGroup.sort_order, options: sort.order.options } }),
        },
      };

      setFormData(updatedFormData); // Update formData with API response values
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) {
        setIsRestricted(true);
      } else {
        console.error('Error fetching data:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    setEmbedSettings(toEmbedSettings(formValues.exampleSettingGroup));
  };

  // Preview the embed. This page's content security policy forbids inline
  // scripts, so instead of running the embed code above it sets the same
  // HLSettings and loads the same /embed script (allowed in public/_headers).
  useEffect(() => {
    const output = outputRef.current;
    if (isRestricted || !output) {
      return undefined;
    }

    // A fresh container each time, so the previous preview's shadow root goes
    const container = document.createElement('div');
    container.id = 'hl-container';
    output.replaceChildren(container);

    window.HLSettings = embedSettings;
    const script = document.createElement('script');
    script.src = embedScriptUrl;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
      container.remove();
    };
  }, [embedSettings, isRestricted]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="jobs-embed-container">
      {isRestricted ? (
        <div className="card" style={{ width: '100%', padding: '20px' }}>
          <h4 className="card-title">Jobs Embed</h4>
          <div className="card-section">
            Jobs embed is available on Business and Enterprise accounts.
          </div>
        </div>
      ) : (
      <>
      {/* Embed Form/Options */}
      <div className="embed-form-section">
        <div className="card" style={{ width: '100%', padding: '20px' }}>
          <h4 className="card-title">Embed Form/Options</h4>
          <div className="card-section">
            <form onSubmit={handleSubmit}>
              {Object.keys(formValues).map((key) => (
                <div key={key} className="form-group">
                  <h5 className="form-header">{key.toUpperCase()}</h5>
                  <div className="grid-x grid-margin-x">
                    {Object.keys(formValues[key]).map((label) => {
                      const formSetting = formData[key] && formData[key][label];
                      const value = formValues[key][label];
                      let inputElement;

                      if (!formSetting) return null;

                      switch (formSetting.type) {
                        case 'text':
                          inputElement = (
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => handleInputChange(key, label, e.target.value)}
                            />
                          );
                          break;
                        case 'select':
                          const optionsArray = Object.entries(formSetting.options).map(([value, label]) => ({ value, label }));

                          inputElement = (
                            <Select
                              isLoading={isLoading}
                              options={optionsArray}
                              onChange={(selected) => 
                                formSetting.multiple  // Check if it's a multi-select
                                  ? handleInputChange('exampleSettingGroup', label, selected) // Pass the array of selected options
                                  : handleInputChange('exampleSettingGroup', label, selected.value) // Pass the single selected value
                              }
                              isMulti={formSetting.multiple}
                            />
                          );
                          break;
                        case 'radio':
                          inputElement = formSetting.options.map((opt) => (
                            <label key={opt}>
                              <input
                                type="radio"
                                name={`${key}.${label}`}
                                value={opt}
                                checked={value === opt}
                                onChange={(e) => handleInputChange(key, label, e.target.value)}
                              />
                              {opt}
                            </label>
                          ));
                          break;
                        default:
                          inputElement = null;
                      }

                      return (
                        <div className="cell small-12 large-6" key={label}>
                          <label>{label === 'brands' ? 'organizations' : label}</label>
                          {inputElement}
                          {formSetting.desc && (
                            <div className="form-desc">
                              <p>
                                {formSetting.desc.replace(/<[^>]*>?/gm, '')}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button type="submit" className="button submit-button">
                Submit Form
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Embed Code & Output */}
      <div className="embed-code-output-section">
        <div className="card" style={{ width: '100%', padding: '20px' }}>
          <h4 className="card-title">Embed Code</h4>
          <div className="card-section">
            <textarea rows="10" id="scriptArea" readOnly style={{ width: '100%' }} value={buildEmbedCode(embedSettings)} />
          </div>
        </div>

        <div className="card" style={{ width: '100%', padding: '20px' }}>
          <h4 className="card-title">Embed Output</h4>
          <div className="card-section" id="hl-output-section">
            <div className="row" ref={outputRef} />
          </div>
        </div>
      </div>
      </>
      )}

      <style jsx>{`
        .jobs-embed-container {
          display: flex;
          gap: 20px;
          margin: 20px;
        }

        .embed-form-section {
          flex: 1;
          min-width: 300px;
          max-width: 40%;
        }

        .embed-code-output-section {
          flex: 2;
          min-width: 300px;
          max-width: 100%;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-header {
          font-weight: bold;
          margin-bottom: 10px;
        }

        .form-desc {
          margin-top: 5px;
          color: #6c757d;
        }

        .submit-button {
          margin-top: 20px;
          float: right;
        }
      `}</style>
    </div>
  );
};

export default JobsEmbedPage;
